import { Injectable, EventEmitter, inject } from '@angular/core';
import { BaseAppSyncService } from '../base-app-sync.service';
import { BehaviorSubject, forkJoin, from, Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { getFacilityTypes } from '../../graphql/facilities/get-facility-types';
import { getFacilityHistories } from '../../graphql/facilities/get-facility-history';
import {
  Facility,
  FacilityConnection,
  FacilitySortOrQuery,
} from '../../model/locations/facility';
import { getUserFacilities } from '../../graphql/facilities/get-user-facilities';
import { getFacilities } from '../../graphql/facilities/get-facilities';
import { createFacility } from '../../graphql/facilities/create-facility';
import { updateFacility } from '../../graphql/facilities/update-facility';
import { deleteFacility } from '../../graphql/facilities/delete-facility';
import { queryForFacilities } from '../../graphql/facilities/query-for-facilities';
import { getFacility } from '../../graphql/facilities/get-facility';
import { FacilityNotepadCategory } from '../../model/locations/facility-notepad-category';
import { FacilityBuilderService } from '../builder/facility-builder.service';
import { getFacilityNotepadCategories } from '../../graphql/facilities/get-facility-notepad-categories';
import {
  CreateFacilityNotepadEntry,
  UpdateFacilityNotepadEntry,
  FacilityNotepadEntry,
} from '../../model/locations/facility-notepad-entry';
import { createFacilityNotepadEntry } from '../../graphql/facilities/create-facility-notepad-entry';
import { updateFacilityNotepadEntry } from '../../graphql/facilities/edit-facility-notepad-entry';
import { deleteFacilityNotepadEntry } from '../../graphql/facilities/delete-facility-notepad-entry';
import { addContactListToFacility } from '../../graphql/facilities/add-contact-list-to-facility';
import {
  FacilitySignIn,
  FacilitySignInConnection,
  FacilitySignInSortOrQuery,
} from '../../model/locations/facility-sign-in';
import { queryForFacilitySignIns } from '../../graphql/facilities/query-for-facility-sign-ins';
import { deleteContactListFromFacility } from 'src/app/graphql/facilities/delete-contact-list-from-facility';
import { Audit } from 'src/app/model/common/audit';
import { buildAuditHistory } from 'src/app/core/functions/common-functions';
import { ConfigItem } from 'src/app/model/common/config-item';
import { getFacilityQRCodeURL } from 'src/app/graphql/facilities/get-facility-qr-code-url';
import { getSignedInPeople } from 'src/app/graphql/facilities/get-signed-in-people';

export type FacilityContactListType =
  | 'contactList'
  | 'managementContactList'
  | 'additionalResourceList';

@Injectable({
  providedIn: 'root',
})
export class FacilityApiService extends BaseAppSyncService {
  protected _facilityBuilder = inject(FacilityBuilderService);

  availableFacilityTypesLoadedEvent: EventEmitter<any> =
    new EventEmitter<any>();

  facilities: BehaviorSubject<Facility[]> = new BehaviorSubject<Facility[]>([]);
  facilitiesLastUpdate: Date = new Date(1970, 0, 1);

  getFacilities(): Observable<Facility[]> {
    // Run the re-load function every time we try to getFacilities
    // this way we always try and refresh the cache
    // If we find new facilities with this call, since we are using a
    // behaviorsubject, the other usages of the place should get the next
    // set of them and update their ui lists when the dropdown is opened
    this.loadFacilities();

    return this.facilities;
  }

  loadFacilities(forceRefresh = false): void {
    this.query(
      getFacilities,
      {
        lastCacheUpdate: forceRefresh
          ? null
          : this.facilitiesLastUpdate.toISOString(),
      },
      (items: any[]) =>
        items?.map((i) => this._facilityBuilder.buildFacility(i)) ?? [],
      'getFacilities'
    ).subscribe((response: Facility[]) => {
      this.facilitiesLastUpdate = new Date();
      if (response.length !== 0) {
        this.facilities.next([...response]);
      }
    });
  }

  getFacilityTypes(): Observable<ConfigItem[]> {
    return this.query(
      getFacilityTypes,
      {},
      (i: any[]) => i,
      'getFacilityTypes',
      'cache-first'
    );
  }

  createFacility(facilityInput: any): Observable<Facility> {
    return this.mutate(
      createFacility,
      { input: facilityInput },
      (i: any) => this._facilityBuilder.buildFacility(i),
      'createFacility'
    ).pipe(tap(() => this.loadFacilities()));
  }

  updateFacility(id: string, facilityInput: any): Observable<Facility> {
    return this.mutate(
      updateFacility,
      { id, input: facilityInput },
      (i: any) => this._facilityBuilder.buildFacility(i),
      'updateFacility'
    ).pipe(tap(() => this.loadFacilities()));
  }

  addContactListToFacility(
    id: string,
    contactListId: string,
    contactListType: FacilityContactListType
  ): Observable<Facility> {
    return this.mutate(
      addContactListToFacility,
      { id, contactListId, contactListType },
      (i: any) => this._facilityBuilder.buildFacility(i, []),
      'addContactListToFacility'
    );
  }

  deleteContactListFromFacility(
    id: string,
    contactListId: string
  ): Observable<Facility> {
    return this.mutate(
      deleteContactListFromFacility,
      { id, contactListId },
      (i: any) => this._facilityBuilder.buildFacility(i, []),
      'deleteContactListFromFacility'
    );
  }

  deleteFacility(id: string): Observable<boolean> {
    return this.mutate(
      deleteFacility,
      { id },
      (i: any) => i,
      'deleteFacility'
    ).pipe(tap(() => this.loadFacilities()));
  }

  getFacility(id: string): Observable<Facility> {
    return this.query(
      getFacility,
      { id },
      (i: any) => this._facilityBuilder.buildFacility(i),
      'getFacility'
    );
  }

  queryForFacilities(
    limit: number,
    page: number,
    query: FacilitySortOrQuery,
    sort: FacilitySortOrQuery
  ): Observable<FacilityConnection> {
    return this.query(
      queryForFacilities,
      { limit, page, query, sort },
      (i: any) => this._facilityBuilder.buildFacilityConnection(i),
      'queryForFacilities'
    );
  }

  getFacilityHistories(id: string): Observable<Audit[]> {
    return this.query(
      getFacilityHistories,
      { id },
      (histories: any[]) => buildAuditHistory(histories),
      'getFacilityHistories'
    );
  }

  getUserFacilities(
    email: string,
    includeDirectReports: boolean
  ): Observable<Facility[]> {
    return this.query(
      getUserFacilities,
      { email, includeDirectReports },
      (items: any[]) =>
        items.map((i) => this._facilityBuilder.buildFacility(i)),
      'getUserFacilities'
    );
  }

  getFacilityNotepadCategories(): Observable<FacilityNotepadCategory[]> {
    return this.query(
      getFacilityNotepadCategories,
      {},
      (items: any[]) =>
        items.map((i) => this._facilityBuilder.buildFacilityNotepadCategory(i)),
      'getFacilityNotepadCategories',
      'cache-first'
    );
  }

  createFacilityNotepadEntry(
    input: CreateFacilityNotepadEntry
  ): Observable<FacilityNotepadEntry> {
    return this.mutate(
      createFacilityNotepadEntry,
      { input },
      (i: any) => this._facilityBuilder.buildFacilityNotepadEntry(i),
      'createFacilityNotepadEntry'
    );
  }

  updateFacilityNotepadEntry(
    id: string,
    input: UpdateFacilityNotepadEntry
  ): Observable<FacilityNotepadEntry> {
    return this.mutate(
      updateFacilityNotepadEntry,
      { id, input },
      (i: any) => this._facilityBuilder.buildFacilityNotepadEntry(i),
      'updateFacilityNotepadEntry'
    );
  }

  deleteFacilityNotepadEntry(id: string): Observable<boolean> {
    return this.mutate(
      deleteFacilityNotepadEntry,
      { id },
      (item: { value: boolean }) => item.value,
      'deleteFacilityNotepadEntry'
    );
  }

  queryForFacilitySignIns(
    page: number,
    limit: number,
    query: FacilitySignInSortOrQuery,
    sort: FacilitySignInSortOrQuery
  ): Observable<FacilitySignInConnection> {
    return this.query(
      queryForFacilitySignIns,
      { page, limit, query, sort },
      (response: any) => {
        return {
          items: response.items.map((value: any) =>
            this._facilityBuilder.buildFacilitySignInSearch(value)
          ),
          totalRecords: response.totalRecords,
        };
      },
      'queryForFacilitySignIns'
    );
  }

  getFacilityQRCodeURL(facilityUid: string): Observable<string> {
    return this.query(
      getFacilityQRCodeURL,
      { facilityUid },
      (i: any) => i,
      'getFacilityQRCodeURL'
    );
  }

  getSignedInPeople(facilityId: string): Observable<FacilitySignIn[]> {
    return this.query(
      getSignedInPeople,
      { facilityId },
      (items: any[]) =>
        items.map((i) => this._facilityBuilder.buildFacilitySignIn(i)),
      'getSignedInPeople'
    );
  }
}
